import React, { useState, useEffect, useRef } from "react";
import axiosClient from "../AxiosClient";
import { Footer, Header, AlertToRegistre, SearchDoctorCard, Spinner } from "../Components";
import "../Assets/Css/HomeCss/SearchDoctors.css";
import "../Assets/Css/ReactSelect.css";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import FooterTopBar from "../Components/FooterTopBar";
import FAQSection from '../Components/FAQSection';
import AppointmentFilterPanel from "../Components/AppointmentFilterPanel";
import LoadMoreButton from "../Components/LoadMoreButton";
import Select from "react-select";
import { matchesSpecialty } from "../Services/SpecializationService";

const DEFAULT_COUNTRY = "PK";

const emptyForm = {
  city_id: "",
  specialite: [],
  gender: "",
  availability: [],
  fee_range: "",
  consultation_type: "",
  page: 1
};

// Render dropdown menus on top of everything so they never slide under other content
const selectPortalProps = {
  menuPortalTarget: typeof document !== "undefined" ? document.body : null,
  menuPosition: "absolute",
};

const selectStyles = {
  control: (base) => ({
    ...base,
    minHeight: '48px',
  }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

const formatSpecialities = (data) => {
  if (!Array.isArray(data)) {
    if (data?.specializations && Array.isArray(data.specializations)) {
      data = data.specializations;
    } else if (data?.data && Array.isArray(data.data)) {
      data = data.data;
    } else {
      console.error("Unexpected API response format:", data);
      data = [];
    }
  }
  return data.map(item => ({
    value: item.id || item.name || item,
    label: item.name || item
  }));
};

const fetchCitiesForCountry = (iso2) =>
  axiosClient.get(`/locations/country-code/${iso2}/cities`)
    .then((res) => (res.data && res.data.success
      ? res.data.data.map((c) => ({ value: c.id, label: c.name }))
      : []))
    .catch((err) => {
      console.error("Error fetching cities:", err);
      return [];
    });

const detectCountry = async () => {
  // Primary: ipwho.is returns { country_code: "PK", ... }
  try {
    const geoRes = await fetch("https://ipwho.is/");
    const geoData = await geoRes.json();
    if (geoData && geoData.success !== false && geoData.country_code) {
      return geoData.country_code;
    }
  } catch (err) {
    console.error("Error detecting country (ipwho.is):", err);
  }

  // Fallback: geojs.io returns { country: "PK", ... }
  try {
    const geoRes = await fetch("https://get.geojs.io/v1/ip/country.json");
    const geoData = await geoRes.json();
    if (geoData.country) return geoData.country;
  } catch (err) {
    console.error("Error detecting country (geojs):", err);
  }

  return DEFAULT_COUNTRY;
};

const SearchDoctors = () => {
  document.title = "Find a Doctor - Famra Cure";

  const AuthUserData = useSelector((state) => state.authUser);
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [specialitiesList, setSpecialitiesList] = useState([]);
  const [citiesList, setCitiesList] = useState([]);
  const [selectedCity, setSelectedCity] = useState(null);
  const [dataForm, setDataForm] = useState(emptyForm);
  const [dataSearch, setDataSearch] = useState([]);
  const [hasMore, setHasMore] = useState(true);

  const specialitiesRequest = useRef(null);
  const citiesRequest = useRef(null);
  const latestSearch = useRef(0);

  const loadSpecialities = () => {
    if (!specialitiesRequest.current) {
      specialitiesRequest.current = axiosClient.get("/specializations")
        .then((res) => formatSpecialities(res.data))
        .catch((err) => {
          console.error("Error fetching specializations:", err);
          return [];
        })
        .then((list) => {
          setSpecialitiesList(list);
          return list;
        });
    }
    return specialitiesRequest.current;
  };

  const loadCities = () => {
    if (!citiesRequest.current) {
      citiesRequest.current = detectCountry()
        .then((iso2) => fetchCitiesForCountry(iso2))
        .then((list) => {
          setCitiesList(list);
          return list;
        });
    }
    return citiesRequest.current;
  };

  // City links (e.g. footer) name Pakistani cities, so fall back to the Pakistan list
  const findCityByName = async (name) => {
    const byName = (list) =>
      list.find((c) => c.label.toLowerCase() === name.toLowerCase());
    const match = byName(await loadCities());
    if (match) return match;

    const pkCities = await fetchCitiesForCountry(DEFAULT_COUNTRY);
    const pkMatch = byName(pkCities);
    if (pkMatch) {
      setCitiesList(pkCities);
      citiesRequest.current = Promise.resolve(pkCities);
    }
    return pkMatch || null;
  };

  const fetchDoctors = (formData = {}) => {
    const searchId = ++latestSearch.current;
    setLoading(true);
    const payload = {
      ...formData,
      specialite: (formData.specialite || []).map(
        (s) => (s && typeof s === "object" ? s.value : s)
      ),
    };
    axiosClient.post("/search/doctors", payload)
      .then((res) => {
        // Ignore responses from searches that have since been replaced
        if (searchId !== latestSearch.current) return;
        setHasMore(res.data.DataSearch.next_page_url !== null);
        if (formData.page === 1) {
          setDataSearch(res.data.DataSearch.data);
        } else {
          setDataSearch(prevData => [...prevData, ...res.data.DataSearch.data]);
        }
      })
      .catch((err) => {
        console.error(err);
        if (searchId === latestSearch.current && formData.page === 1) {
          setDataSearch([]);
          setHasMore(false);
        }
      })
      .finally(() => {
        if (searchId === latestSearch.current) setLoading(false);
      });
  };

  useEffect(() => {
    loadSpecialities();
    loadCities();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Apply filters from the URL (?specialty=&city=&type=) or from navigation state
  useEffect(() => {
    let cancelled = false;

    const applyFilters = async () => {
      const params = new URLSearchParams(location.search);
      const form = {
        ...emptyForm,
        city_id: location.state?.city_id || "",
        specialite: location.state?.specialite || [],
        consultation_type: params.get("type") || "",
      };

      const specialty = params.get("specialty");
      if (specialty) {
        const list = await loadSpecialities();
        form.specialite = list
          .filter((opt) => matchesSpecialty(opt.label, specialty))
          .map((opt) => opt.value);
      }

      const cityName = params.get("city");
      let cityOption = null;
      if (cityName) {
        cityOption = await findCityByName(cityName);
        form.city_id = cityOption?.value || "";
      }

      if (cancelled) return;
      setSelectedCity(cityOption);
      setDataForm(form);
      fetchDoctors(form);
    };

    applyFilters();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  // Resolve the pre-selected city (from navigation state) once cities load
  useEffect(() => {
    if (!selectedCity && dataForm.city_id && citiesList.length > 0) {
      const match = citiesList.find((c) => String(c.value) === String(dataForm.city_id));
      if (match) setSelectedCity(match);
    }
  }, [citiesList, dataForm.city_id, selectedCity]);

  const updateFilters = (changes) => {
    const updatedForm = { ...dataForm, ...changes, page: 1 };
    setDataForm(updatedForm);
    fetchDoctors(updatedForm);
  };

  const handleSubmitData = (e) => {
    e.preventDefault();
    updateFilters({});
  };

  const handleCityChange = (selectedOption) => {
    setSelectedCity(selectedOption);
    updateFilters({ city_id: selectedOption?.value || "" });
  };

  const handleSpecialityChange = (selectedOptions) => {
    updateFilters({ specialite: (selectedOptions || []).map((o) => o.value) });
  };

  const handleLoadMore = () => {
    const updatedData = { ...dataForm, page: dataForm.page + 1 };
    setDataForm(updatedData);
    fetchDoctors(updatedData);
  };

  const selectedSpecialities = specialitiesList.filter((opt) =>
    (dataForm.specialite || []).map(String).includes(String(opt.value))
  );

  const [showAlertToRegistre, setSowAlertToRegistre] = useState(AuthUserData.showAlertToAuth);
  const [showFilters, setShowFilters] = useState(false);

  return (
    <>
      <Header />
      <section>
        <div className="_img_cover">
          <div className="search_bar">
            <form onSubmit={handleSubmitData}>
              <div className="search_input">
                <label htmlFor="Location" className="label_search">
                  Location
                  <i className="fas fa-map-marker-alt"></i>
                </label>
                <Select
                  inputId="Location"
                  name="city_id"
                  options={citiesList}
                  value={selectedCity}
                  onChange={handleCityChange}
                  placeholder="Choose City"
                  isClearable
                  className="react-select-container"
                  classNamePrefix="react-select"
                  styles={selectStyles}
                  {...selectPortalProps}
                />
              </div>
              <div className="search_input">
                <label htmlFor="Specialite" className="label_search">
                  Specialty
                  <i className="fas fa-stethoscope"></i>
                </label>
                <Select
                  inputId="Specialite"
                  name="specialite"
                  isMulti
                  options={specialitiesList}
                  value={selectedSpecialities}
                  onChange={handleSpecialityChange}
                  placeholder="Select Specialties..."
                  className="react-select-container"
                  classNamePrefix="react-select"
                  styles={selectStyles}
                  {...selectPortalProps}
                />
              </div>
              <button className="btn_search btn_bg_primary">Search</button>
            </form>
          </div>
        </div>
      </section>

      <main className="_container mt-4 mb-4">
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {/* Filter Panel - Full width on mobile, sidebar on larger screens */}
          <div className="col-span-1 md:col-span-1 lg:col-span-1 mb-4 md:mb-0">
            {/* Mobile Filter Toggle Button */}
            <div className="block md:hidden mb-4">
              <button
                className="w-full py-2 px-4 bg-blue-600 text-white rounded-md flex items-center justify-center"
                onClick={() => setShowFilters(!showFilters)}
              >
                <span>Filter Options</span>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-2" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M3 3a1 1 0 011-1h12a1 1 0 011 1v3a1 1 0 01-.293.707L12 11.414V15a1 1 0 01-.293.707l-2 2A1 1 0 018 17v-5.586L3.293 6.707A1 1 0 013 6V3z" clipRule="evenodd" />
                </svg>
              </button>
            </div>

            {/* Filter Panel - Hidden on mobile until toggled, always visible on larger screens */}
            <div className={`${showFilters ? "block" : "hidden"} md:block`}>
              <AppointmentFilterPanel
                dataForm={dataForm}
                updateFilters={updateFilters}
                specialitiesList={specialitiesList}
                selectedSpecialities={selectedSpecialities}
                handleSpecialityChange={handleSpecialityChange}
                citiesList={citiesList}
                selectedCity={selectedCity}
                handleCityChange={handleCityChange}
              />
            </div>
          </div>

          {/* Right Column - Doctor Cards */}
          <div className="col-span-1 md:col-span-2 lg:col-span-3">
            {loading && dataForm.page === 1 ? (
              <div className="w-full flex items-center justify-center mb-10">
                <Spinner />
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-4">
                {dataSearch.length ? (
                  dataSearch.map((info) => (
                    <SearchDoctorCard key={info.id} {...info} />
                  ))
                ) : (
                  <p>No doctors found</p>
                )}
              </div>
            )}

            {/* Load More Button */}
            {!loading && hasMore && dataSearch.length > 0 && (
              <div className="mt-4">
                <LoadMoreButton onClick={handleLoadMore} />
              </div>
            )}
          </div>
        </div>
      </main>

      <FAQSection />
      <FooterTopBar />
      <Footer />
      <AlertToRegistre
        showAlertToRegistre={showAlertToRegistre}
        setSowAlertToRegistre={setSowAlertToRegistre}
      />
    </>
  );
};

export default SearchDoctors;
